import React from "react";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { useNavigate } from "react-router";
import C from "classnames";

import { VariantType } from "../contentCardTypes";
import { buildRoute } from "../../../../constants/routes";

import S from "./AssessmentCard.module.scss";

export interface AssessmentCardProps {
  cardSize?: "large";
  variant?: VariantType;
  exam?: {
    title: string;
    description?: string;
    status?: string;
    onLaunch: () => void;
  };
  /** Prefix used to build data-testids for the card and its buttons. */
  testId?: string;
}

export const AssessmentCard: React.FC<AssessmentCardProps> = ({
  cardSize = "large",
  variant = "indigo",
  exam,
  testId = "assessment-card",
}) => {
  const navigate = useNavigate();

  const navigateToAssessments = (): void => {
    navigate(buildRoute("Learn.routes.assessments.route"));
  };

  const isLargeCard = cardSize === "large";
  const cardSizeClass = isLargeCard ? S.largeCard : S.smallCard;
  const variantClass = variant ? S[variant] : undefined;

  return (
    <div className={C(S.assessmentCardWrapper, cardSizeClass, variantClass)} data-testid={testId}>
      <div className={C(S.assessmentCardContainer, cardSizeClass, variantClass)}>
        <div className={S.detailsColumn}>
          <div className="flex items-center gap-4">
            <Tag value="Assessment" severity="contrast" rounded={true} />
          </div>
          <h3 className={`atlas-h1-alt white-text`}>Assess your knowledge and progress</h3>
          <p className="body-md white-text">
            Assess your proficiency in a range of topics. Retake assessments to track your progress
            over time and get recommended lessons and paths tailored for you.
          </p>
          <div>
            <Button
              label="Take assessment"
              data-testid={`${testId}-take-button`}
              onClick={navigateToAssessments}
            />
          </div>
        </div>
        {exam && (
          <div className={S.examColumn}>
            <p className="uppercase white-text mb-2">Your Exam</p>
            <div className={C(S.examCard, "flex flex-col gap-2 p-4 bg-surface-100 rounded-lg")}>
              <h4 className="atlas-h4 mb-1">{exam.title}</h4>
              {exam.description && <p className="body-sm mb-1">{exam.description}</p>}
              {exam.status && <Tag value={exam.status} severity="info" className="mb-1" />}
              <Button
                label="Resume Exam"
                data-testid={`${testId}-resume-button`}
                onClick={exam.onLaunch}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
